import { rootApi } from '../rootApi';

export const announcementApi = rootApi.injectEndpoints({
    endpoints: (builder) => ({
        getAll: builder.query({
            query: (params) => {
                // Base parameters
                const queryParams = {
                    PageNumber: params.PageNumber !== undefined ? params.PageNumber : 0,
                    PageSize: params.PageSize || 10
                };

                // Only add IsActive if it has a value
                if (params.IsActive !== undefined && params.IsActive !== null) {
                    queryParams.IsActive = params.IsActive;
                }

                // Only add ShortName if it has a value
                if (params.AnnouncementType) {
                    queryParams.AnnouncementType = params.AnnouncementType;
                }

                if (params.LanguageId) {
                    queryParams.LanguageId = params.LanguageId;
                }
                return {
                    url: 'v1/announcements',
                    params: queryParams
                };
            },
            transformResponse: (response) => {
                // Check if response has the expected structure with data property
                if (response && typeof response === 'object') {
                    // New structure: { data: { items: [...], totalCount: number } }
                    if (response.data && response.data.items && Array.isArray(response.data.items)) {
                        return {
                            items: response.data.items,
                            totalCount: response.data.totalCount
                        };
                    }

                    // Old structure: { data: [...] }
                    if (response.data && Array.isArray(response.data)) {
                        return {
                            items: response.data,
                            totalCount: response.data.length
                        };
                    }

                    // If response itself is an array, return it directly
                    if (Array.isArray(response)) {
                        return {
                            items: response,
                            totalCount: response.length
                        };
                    }
                }

                // Fallback: return empty array if structure doesn't match
                console.warn('⚠️ Response structure not as expected, returning empty array');
                return {
                    items: [],
                    totalCount: 0
                };
            },
            // Keep track of API state for debugging
            onQueryStarted: async (arg, { queryFulfilled }) => {
                try {
                    const result = await queryFulfilled;
                } catch (error) {
                    console.error('❌ getAll query failed:', error);
                }
            },
            providesTags: ['Announcement']
        }),
        getAnnouncementByDealerId: builder.query({
            query: (params) => {
                // params beklenen alanlar:
                // { DealerId, PageNumber, PageSize, LanguageId }
                return {
                    url: 'v1/announcements/getbydealerid', // <-- GET endpoint (case önemli olabilir)
                    method: 'GET',
                    params // QueryString'e eklenir
                };
            },
            transformResponse: (response) => {
                // Beklenen response:
                // { isSuccessful, data: { items: [...], totalCount }, ... }
                const box = response?.data ?? response;
                const items = Array.isArray(box?.items) ? box.items : [];
                const totalCount = box?.totalCount ?? items.length;
                return { items, totalCount, raw: response };
            },
            providesTags: ['Announcement']
        }),

        createAnnouncement: builder.mutation({
            query: (data) => ({
                url: 'v1/announcements',
                method: 'POST',
                body: data
            }),
            invalidatesTags: ['Announcement']
        }),

        updateAnnouncement: builder.mutation({
            query: (data) => ({
                url: 'v1/announcements',
                method: 'PATCH',
                body: data
            }),
            invalidatesTags: ['Announcement']
        }),

        publishAnnouncement: builder.mutation({
            query: (data) => ({
                url: 'v1/announcements/publish',
                method: 'PATCH',
                body: data
            }),
            invalidatesTags: ['Announcement']
        }),

        updateAnnouncementReadStatus: builder.mutation({
            query: (data) => ({
                url: 'v1/announcements/updatereadstatus',
                method: 'PATCH',
                body: data
            })
        }),

        deleteAnnouncement: builder.mutation({
            query: (id) => ({
                url: `v1/announcements/delete`,
                method: 'DELETE',
                body: { id }
            }),
            invalidatesTags: ['Announcement']
        }),
        uploadAnnouncementDocument: builder.mutation({
            query: ({ AnnouncementId, file, description }) => {
                const formData = new FormData();
                formData.append('AnnouncementId', AnnouncementId);
                formData.append('File', file);
                formData.append('Description', description);
                return {
                    url: 'v1/announcements/uploaddocument',
                    method: 'POST',
                    body: formData,
                    formData: true
                };
            },
            invalidatesTags: (result, error, { AnnouncementId }) => [{ type: 'AnnouncementDocument', id: AnnouncementId }]
        }),
        getAnnouncementDocumentsById: builder.query({
            query: (id) => ({
                url: `v1/announcements/getdocumentsbyid?id=${id}`,
                method: 'GET'
            }),
            providesTags: (result, error, id) => [{ type: 'AnnouncementDocument', id }]
        }),

        deleteAnnouncementDocument: builder.mutation({
            query: (data) => ({
                url: 'v1/announcements/DeleteDocument',
                method: 'DELETE',
                body: data
            }),
            invalidatesTags: (result, error, { AnnouncementId }) => [{ type: 'VehicleWarrantyPeriodDocument', id: AnnouncementId }]
        }),
        getUnreadAnnouncementCount: builder.query({
            query: () => ({
                url: 'v1/announcements/GetUnreadAnnouncementCount',
                method: 'GET'
            }),
            transformResponse: (response) => {
                return response?.data ?? response ?? 0;
            },
            providesTags: ['Announcement']
        })
    })
});

export const {
    useGetAllQuery,
    useCreateAnnouncementMutation,
    useUpdateAnnouncementMutation,
    usePublishAnnouncementMutation,
    useUpdateAnnouncementReadStatusMutation,
    useDeleteAnnouncementMutation,
    useUploadAnnouncementDocumentMutation,
    useGetAnnouncementDocumentsByIdQuery,
    useDeleteAnnouncementDocumentMutation,
    useGetAnnouncementByDealerIdQuery,
    useGetUnreadAnnouncementCountQuery
} = announcementApi;
